/** VistoeraTASK-00147 — canonical Canvas image viewport-gating +
 * progressive thumbnail→preview loading primitive.
 *
 * FORENSIC ROOT CAUSE (see `_canvas_image_lazy_load_forensic/forensic-
 * before.json`, real Live-Preview measurement against a 125-node canvas
 * with 69 `<img>` elements): `ImageAssetNode`/`GenerateImageNode` each set
 * a real `web` (≤2000px) variant `<img src>` unconditionally on mount,
 * regardless of React-Flow viewport visibility. Only 9/125 nodes were
 * actually on-screen, yet 35 concurrent `variants/web` requests fired
 * (peak concurrency 43) and ZERO of the 69 `<img>` elements had finished
 * decoding 8.5s after settle — i.e. the long-lived "black box" users see
 * is request/decode-queue saturation from off-screen nodes competing with
 * on-screen ones, NOT a missing thumbnail/preview tier (that 3-tier
 * `thumbnail`/`web`/`original` `SiteAsset` variant system already exists
 * and is already wired up — see `canvasImageQuality.ts`). The fix is a
 * viewport-aware GATE on when the request is even issued, plus a
 * zoom-aware tier choice, plus an explicit MEDIA-loading placeholder
 * state that is fully independent of any generation status.
 *
 * Deliberately a pair of HOOKS (not one big shared `<CanvasImageMedia>`
 * JSX component) — `ImageAssetNode`/`GenerateImageNode` have very
 * different surrounding chrome (local-upload lifecycle, editor overlay,
 * generation status/history, master-meta, …) and forcing one shared
 * component would mean a much larger, riskier rewrite of both. The
 * canonical, shared PRIMITIVE both nodes actually need — "should I even
 * be fetching yet, and if so which tier, with no black flash on upgrade
 * and an explicit loading/error state" — is fully captured here and used
 * identically by both call sites (spec §十七).
 */
import { useCallback, useEffect, useRef, useState } from 'react'

/** Below this on-screen rendered width (CSS px, AFTER the React Flow pane's
 * zoom transform — i.e. what a human viewer actually sees, not the node's
 * logical/unzoomed width), the ≤400px thumbnail variant is visually
 * indistinguishable from the ≤2000px `web` preview but is dramatically
 * cheaper to fetch + decode, so it is always preferred until the node is
 * rendered large enough that a viewer could actually perceive the extra
 * detail (spec §五 "缩放等级也要参与 Asset Variant 选择"). */
export const DEFAULT_THUMBNAIL_MAX_RENDER_PX = 220

/** Bounded prefetch margin (spec §四) — nodes within this many CSS px of
 * the real browser viewport start loading slightly BEFORE they are
 * actually on-screen (so a small pan doesn't show a visible pop-in), but
 * nodes far outside it never issue a network request at all. */
export const DEFAULT_VIEWPORT_PREFETCH_MARGIN_PX = 600

export type CanvasProgressiveImageTier = 'none' | 'thumbnail' | 'preview'

/** Pure — trivially unit-testable. */
export function resolveDesiredImageTier(
  renderedWidthPx: number | null,
  thumbnailMaxRenderPx: number = DEFAULT_THUMBNAIL_MAX_RENDER_PX,
): 'thumbnail' | 'preview' {
  if (renderedWidthPx == null) return 'preview'
  if (!Number.isFinite(renderedWidthPx) || renderedWidthPx <= 0) return 'preview'
  return renderedWidthPx <= thumbnailMaxRenderPx ? 'thumbnail' : 'preview'
}

/**
 * VistoeraTASK-00238 — pure, unit-testable "first paint" tier decision.
 *
 * A brand-new `SiteAsset`'s `thumbnail` variant is generated
 * SYNCHRONOUSLY at upload time (near-instant on first request); its
 * `web`/preview variant is intentionally DEFERRED off the upload POST
 * (VistoeraTASK-00135) and materialized on-demand the FIRST time any
 * consumer requests it — measured live on Preview at a FIXED ~3.3-3.8s
 * tax, independent of file size. Never let that tax sit on the very
 * first visible paint of a freshly-created identity: if a `thumbnail`
 * URL is available, ALWAYS paint it first regardless of the desired
 * tier — the caller's existing non-blocking upgrade path promotes to
 * the real desired tier the moment it is ready, with no black flash and
 * no change to final image quality (spec §十三/§十七/§二十六/§二十七).
 */
export function resolveFirstPaintTier(
  desiredTier: 'thumbnail' | 'preview',
  thumbnailUrl: string | null,
): 'thumbnail' | 'preview' {
  if (desiredTier === 'preview' && thumbnailUrl) return 'thumbnail'
  return desiredTier
}

type EntryHandler = (entry: IntersectionObserverEntry) => void
interface ObserverBucket {
  observer: IntersectionObserver
  handlers: Map<Element, EntryHandler>
}
const sharedObservers = new Map<number, ObserverBucket>()

function getSharedObserver(rootMarginPx: number): ObserverBucket | null {
  if (typeof IntersectionObserver === 'undefined') return null
  let bucket = sharedObservers.get(rootMarginPx)
  if (!bucket) {
    const handlers = new Map<Element, EntryHandler>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) handlers.get(entry.target)?.(entry)
      },
      { root: null, rootMargin: `${rootMarginPx}px`, threshold: [0, 0.01] },
    )
    bucket = { observer, handlers }
    sharedObservers.set(rootMarginPx, bucket)
  }
  return bucket
}

export interface CanvasMediaViewportGate {
  setRef: (el: Element | null) => void
  /** STICKY — once true, stays true forever for this hook instance (spec
   * §十四: cancelling/re-gating an already-decided small thumbnail on a
   * brief pan-out thrashes the network for no real memory benefit; only
   * the decision to START loading is gated, not a resolved image torn
   * down again). */
  isNearViewport: boolean
  /** Debounced (150ms) — the node's actual on-screen CSS width right now,
   * or `null` before the first observation lands. */
  renderedWidthPx: number | null
}

/** Reuses ONE shared native `IntersectionObserver` per rootMargin bucket —
 * never a per-node observer, never a polling/rAF-based recalculation loop
 * (spec §四 "不要新增高频全 Canvas intersection recalculation"). Correctly
 * reflects the REAL post-zoom rendered size even though React Flow pans/
 * zooms via a CSS `transform` on an ancestor (not real scroll/resize) —
 * Chromium/Firefox both invalidate intersection observations on ancestor
 * transform changes, so this needs no extra plumbing into React Flow's
 * own `onMove`/viewport state at all. */
export function useCanvasMediaViewportGate(
  rootMarginPx: number = DEFAULT_VIEWPORT_PREFETCH_MARGIN_PX,
): CanvasMediaViewportGate {
  const [isNearViewport, setIsNearViewport] = useState(false)
  const [renderedWidthPx, setRenderedWidthPx] = useState<number | null>(null)
  const elRef = useRef<Element | null>(null)
  const widthDebounceRef = useRef<number | null>(null)

  const setRef = useCallback(
    (el: Element | null) => {
      const bucket = getSharedObserver(rootMarginPx)
      if (!bucket) {
        // No IntersectionObserver support (should not happen in any
        // supported browser) — fail OPEN, never silently show nothing.
        setIsNearViewport(true)
        return
      }
      if (elRef.current && elRef.current !== el) {
        bucket.observer.unobserve(elRef.current)
        bucket.handlers.delete(elRef.current)
      }
      elRef.current = el
      if (!el) return
      bucket.handlers.set(el, (entry) => {
        if (entry.isIntersecting) setIsNearViewport(true)
        const width = entry.boundingClientRect.width
        if (widthDebounceRef.current != null) window.clearTimeout(widthDebounceRef.current)
        widthDebounceRef.current = window.setTimeout(() => setRenderedWidthPx(width), 150)
      })
      bucket.observer.observe(el)
    },
    [rootMarginPx],
  )

  useEffect(() => {
    return () => {
      const bucket = getSharedObserver(rootMarginPx)
      if (bucket && elRef.current) {
        bucket.observer.unobserve(elRef.current)
        bucket.handlers.delete(elRef.current)
      }
      if (widthDebounceRef.current != null) window.clearTimeout(widthDebounceRef.current)
    }
  }, [rootMarginPx])

  return { setRef, isNearViewport, renderedWidthPx }
}

export interface CanvasProgressiveImageState {
  /** Currently COMMITTED `<img src>` — `null` means render a neutral
   * placeholder, never a browser broken-image icon. */
  src: string | null
  tier: CanvasProgressiveImageTier
  /** True while a request for the CURRENT `src` is outstanding — distinct
   * from generation status; drives a "正在加载" placeholder, never
   * "正在生成" (spec §六/§七/§十一). */
  isLoading: boolean
  hasError: boolean
  /** Call from the real `<img onLoad>`. */
  markLoaded: () => void
  /** Call from the real `<img onError>`. */
  markError: () => void
}

/**
 * Viewport-gated + zoom-aware + progressive-upgrade image source resolver.
 * MEDIA STATE ONLY — callers keep their OWN generation-status state fully
 * separate (spec §六); this hook has no idea what "GENERATING" even means.
 */
export function useCanvasProgressiveImageSrc(args: {
  thumbnailUrl: string | null
  previewUrl: string | null
  isNearViewport: boolean
  renderedWidthPx: number | null
  /** Changing this hard-resets all committed state (e.g. a brand new
   * `entityId`/`outputId` — an old asset's bytes must never linger as the
   * "current" src for a completely different asset). */
  resetKey: string | null
  /** Bump to force a genuinely fresh request for the SAME url (explicit
   * user retry — never automatic). */
  retryNonce?: number
  thumbnailMaxRenderPx?: number
}): CanvasProgressiveImageState {
  const {
    thumbnailUrl,
    previewUrl,
    isNearViewport,
    renderedWidthPx,
    resetKey,
    retryNonce = 0,
    thumbnailMaxRenderPx,
  } = args

  const desiredTier: CanvasProgressiveImageTier = !isNearViewport
    ? 'none'
    : resolveDesiredImageTier(renderedWidthPx, thumbnailMaxRenderPx)
  const desiredUrl =
    desiredTier === 'none' ? null : desiredTier === 'thumbnail' ? thumbnailUrl || previewUrl : previewUrl || thumbnailUrl

  const [committedSrc, setCommittedSrc] = useState<string | null>(null)
  const [committedTier, setCommittedTier] = useState<CanvasProgressiveImageTier>('none')
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const preloadImgRef = useRef<HTMLImageElement | null>(null)
  const lastRequestKeyRef = useRef<string | null>(null)
  const lastResetKeyRef = useRef<string | null>(null)
  // VistoeraTASK-00153 forensic root cause — this MUST be a ref, never
  // derived from the `committedSrc` STATE value read inside the second
  // effect below. On the render where `resetKey` changes (e.g. a
  // Generate Image regenerate producing a brand-new `displayOutputId`
  // while an OLD result was already committed/on-screen), BOTH effects
  // run in the SAME commit using THIS render's stale `committedSrc`
  // closure (still the OLD src — the reset effect's `setCommittedSrc
  // (null)` has only been *scheduled*, not yet applied to this closure).
  // The second effect used to branch on `!committedSrc` to decide
  // "first commit for this identity" vs "upgrade an already-shown
  // image" — saw the STALE non-null old src, and incorrectly took the
  // off-DOM preload-and-swap branch (which never sets `isLoading`).
  // React then re-renders with the reset applied (`committedSrc=null`),
  // re-running this same effect (its own `committedSrc` dependency just
  // changed) — but the request-dedup guard below, already stamped by
  // the FIRST (wrong-branch) run, made it bail out before ever taking
  // the direct-commit branch. The orphaned preload `probe` from the
  // first run is then cancelled (cleanup nulls `preloadImgRef.current`)
  // before its `onload` fires, so that late `onload`'s own staleness
  // check (`preloadImgRef.current !== probe`) silently discards it too
  // — `committedSrc` never becomes the new URL, `isLoading` never
  // becomes true/false meaningfully, and the "正在加载" media overlay
  // (`!mediaImage.src || mediaImage.isLoading`) spins forever even
  // though generation genuinely succeeded. Tracking "have we committed
  // at least once for the CURRENT resetKey" via a ref — synchronously
  // flipped to `false` by the reset effect, which always runs BEFORE
  // this effect within the same commit — makes the very first run after
  // a reset correctly take the direct-commit branch, regardless of
  // which render's `committedSrc` closure it happens to see.
  const hasCommittedForResetRef = useRef(false)

  // Hard reset on identity change (new asset / new generation output).
  useEffect(() => {
    if (resetKey === lastResetKeyRef.current) return
    lastResetKeyRef.current = resetKey
    lastRequestKeyRef.current = null
    preloadImgRef.current = null
    hasCommittedForResetRef.current = false
    setCommittedSrc(null)
    setCommittedTier('none')
    setIsLoading(false)
    setHasError(false)
  }, [resetKey])

  useEffect(() => {
    // `none` = viewport gate closed — no progressive fetch/commit (spec §四).
    // TypeScript cannot infer `desiredTier` is `thumbnail|preview` from
    // `desiredUrl` alone; narrow explicitly before tier-only helpers run.
    if (desiredTier === 'none' || !desiredUrl) return
    const requestKey = `${desiredUrl}::${retryNonce}`
    if (requestKey === lastRequestKeyRef.current) return
    lastRequestKeyRef.current = requestKey
    setHasError(false)

    if (!hasCommittedForResetRef.current) {
      // First commit for the CURRENT `resetKey` — nothing valid is
      // on-screen for THIS identity yet (any previously-committed src
      // belongs to a superseded identity and must never block this).
      // Commit directly; the real `<img onLoad>`/`onError` drives the
      // rest via `markLoaded`/`markError`.
      //
      // VistoeraTASK-00238 — forensic root cause of "dropping a large
      // image onto the Canvas feels noticeably slower than uploading the
      // identical file through the Image Picker": a brand-new
      // `SiteAsset`'s `thumbnail` variant is generated SYNCHRONOUSLY at
      // upload time (near-instant on first request), but its `web`
      // (display-preview, <=2000px) variant is intentionally DEFERRED
      // off the upload POST (`asset_runtime_service.create_asset`,
      // `generate_web_variant=False` by default — VistoeraTASK-00135)
      // and only materialized on `get_variant`'s hot GET path the FIRST
      // time anyone actually requests it. Measured live on Preview: that
      // first on-demand `.../variants/web` fetch costs a FIXED ~3.3-3.8s
      // tax (storage materialize + resize + DB commit) that is
      // essentially INDEPENDENT of the original file's size, and — before
      // this fix — it was the very first thing this hook ever requested
      // whenever a freshly-dropped node rendered wider than
      // `thumbnailMaxRenderPx`, directly blocking the node's first
      // visible paint. The Image Picker's gallery tile never pays this
      // tax because it only ever requests the already-eager `thumbnail`.
      // Fix: the very first paint for a brand-new identity always
      // commits the already-eager `thumbnail` (when available) instead
      // of the desired tier, then falls straight into the EXISTING
      // non-blocking upgrade branch below (off-DOM preload + atomic
      // swap, no black flash) to promote to `preview` once the real
      // `web` variant is ready — same final image quality, just no
      // longer on the critical first-paint path (spec §十三/§十七/§二十
      // 六/§二十七). Deliberately does NOT stamp `lastRequestKeyRef` with
      // `desiredUrl`'s key in this branch so the very next effect run
      // still treats `desiredUrl` as a fresh upgrade target.
      hasCommittedForResetRef.current = true
      setIsLoading(true)
      const firstPaintTier = resolveFirstPaintTier(desiredTier, thumbnailUrl)
      if (firstPaintTier !== desiredTier) {
        // firstPaintTier is always 'thumbnail' in this branch (see
        // resolveFirstPaintTier) — thumbnailUrl is guaranteed non-null.
        lastRequestKeyRef.current = `${thumbnailUrl}::${retryNonce}`
        setCommittedSrc(thumbnailUrl)
        setCommittedTier('thumbnail')
      } else {
        setCommittedSrc(desiredUrl)
        setCommittedTier(desiredTier)
      }
      return
    }

    // Upgrading/downgrading an ALREADY-shown image (e.g. thumbnail→web on
    // zoom-in) — preload fully off-DOM first so the swap is atomic and
    // the OLD image stays visible the entire time (no black flash, spec
    // §十一/§十三).
    const probe = new Image()
    preloadImgRef.current = probe
    probe.onload = () => {
      if (preloadImgRef.current !== probe) return
      setCommittedSrc(desiredUrl)
      setCommittedTier(desiredTier)
    }
    probe.onerror = () => {
      /* A background upgrade failing is never a user-facing error — the
       * currently-committed tier just keeps being shown. */
    }
    probe.src = desiredUrl
    return () => {
      if (preloadImgRef.current === probe) preloadImgRef.current = null
    }
  }, [desiredUrl, desiredTier, retryNonce, committedSrc])

  const markLoaded = useCallback(() => {
    setIsLoading(false)
    setHasError(false)
  }, [])
  const markError = useCallback(() => {
    setIsLoading(false)
    setHasError(true)
  }, [])

  return { src: committedSrc, tier: committedTier, isLoading, hasError, markLoaded, markError }
}
